﻿/* eslint-disable no-underscore-dangle */
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import isValidJSON from '@/utilities/kw.valid.json';
import parseBooleanOrString from '@/utilities/kw.parse.boolean.string';
import toSnakeCase from '@/utilities/kw.snake.case';
import KWTracker from './kw.tracker';

interface Attribute {
    key: string;
    value: string | boolean;
}

type FormFieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const FORM_TRACKER_ATTR = 'data-form-tracker';

export default class KWFormTracker extends TKCustomElementFactory {
    trackerElement?: KWTracker;
    event?: string;
    location: string;
    form?: HTMLFormElement;

    attributeList: Attribute[] = [];
    tracker = window._mtm || [];

    constructor() {
        super();

        this.trackerElement = this.closest('kw-tracker') || undefined;
        this.event = this.getAttribute('data-tk-event') || undefined;
        this.location = window.location.href;
        this.form = this.querySelector('form') || undefined;
    }

    async connectedCallback() {
        if (!this.trackerElement) {
            const trackerElement = document.body.querySelector<KWTracker>('kw-tracker');
            if (trackerElement) this.trackerElement = trackerElement;
            else throw new Error('Tracker Element is missing!');
        }
        if (!this.event) throw new Error('Event is not defined');
        this.registerSubmitListener();
    }

    registerSubmitListener() {
        if (!this.form) throw new Error('Form Element is missing!');
        const onSubmit = this.handleSubmit.bind(this);
        this.pushListener({
            event: 'submit',
            element: this.form,
            action: onSubmit,
        });
    }

    handleSubmit(event: SubmitEvent) {
        event.preventDefault();
        this.getAllAttributes();
        this.getFormFields();
        this.pushTracker();
    }

    getAllAttributes() {
        Object.keys(this.dataset).filter((item) => !item.startsWith('tk')).forEach((key) => {
            const value = this.dataset[key];
            if (!value) return;
            const result = isValidJSON(value);

            const attribute: Attribute = {
                key,
                value: result.isValid ? result.json : parseBooleanOrString(value),
            };

            this.attributeList.push(attribute);
        });
    }

    getFormFields() {
        const formFields = this.querySelectorAll<FormFieldElement>(`[${FORM_TRACKER_ATTR}]`);
        formFields.forEach((formField) => {
            const attribute: Attribute = {
                key: formField.getAttribute(FORM_TRACKER_ATTR) || '',
                value: formField.value,
            };
            this.attributeList.push(attribute);
        });
    }

    pushTracker() {
        const item = {
            event: this.event,
            location: this.location,
            ...this.attributeList.reduce<Record<string, string | boolean>>((acc, attr) => {
                const key = toSnakeCase(attr.key);
                acc[key] = attr.value;
                return acc;
            }, {}),
        };
        this.tracker.push(item);
    }
}